import * as React from 'react';
import type { Extend } from '@mentimeter/ragnar-dsc';
import { ChevronDownIcon } from '@mentimeter/ragnar-visuals';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Collapsable } from '@mentimeter/ragnar-ui/collapsable';
import { Link } from '../actions';

export interface Props {
  name: string;
  href: string;
  active?: boolean;
  onClickChevron?: (slug: string) => void;
  isExpanded?: boolean;
  openDropdownLabel?: string;
  subitems?: Array<{
    name: string;
    href: string;
    active?: boolean;
  }>;
}

const getLinkStyleProps = (active: boolean | undefined) =>
  ({
    active: false,
    color: active ? 'primary' : 'text',
    fontSize: 2,
    underline: false,
  }) as const;

const getCommonExtendStyles: Extend = ({ theme }) => ({
  ':focus': {
    color: theme.colors.primary,
    textDecoration: 'none',
    opacity: 0.75,
  },
  '&:first-child': {
    marginTop: '1px',
  },
  '&:last-child': {
    marginBottom: '1px',
  },
});

const NavigationItem = ({
  name,
  href,
  active,
  onClickChevron,
  isExpanded,
  openDropdownLabel = 'Open dropdown',
  subitems,
}: Props) => {
  const hasSubitems = subitems && subitems.length > 0;
  const handleToggleExpand = () => {
    onClickChevron?.(name);
  };

  return (
    <Box mb="space4" width="100%">
      {hasSubitems ? (
        <>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Link
              {...getLinkStyleProps(active)}
              fontWeight="semiBold"
              href={href}
              ml="1px"
              extend={({ theme }) => ({
                ...getCommonExtendStyles({ theme }),
                ':hover': {
                  color: theme.colors.primary,
                  textDecoration: 'none',
                },
              })}
            >
              {name}
            </Link>
            <Clickable
              aria-label={openDropdownLabel}
              aria-expanded={isExpanded}
              ml="space2"
              mr="3px"
              onClick={handleToggleExpand}
              extend={() => ({
                '@media (hover: hover)': {
                  ':hover': { opacity: 1 },
                },
                ':active': { opacity: 1 },
              })}
            >
              <Box
                extend={({ theme }) => ({
                  transition: `transform ${theme.durations[1]}s ease`,
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                })}
              >
                <ChevronDownIcon color={active ? 'primary' : 'text'} size={2} />
              </Box>
            </Clickable>
          </Box>
          <Collapsable show={isExpanded ?? false}>
            <Box mt="space4">
              {subitems.map((item) => (
                <Link
                  {...getLinkStyleProps(item.active)}
                  fontWeight="regular"
                  href={item.href}
                  mb="space4"
                  ml="space8"
                  mr="1px"
                  key={item.href}
                  tabIndex={isExpanded ? 0 : -1} // If menu is hidden, links should not be focusable
                  extend={({ theme }) => ({
                    ...getCommonExtendStyles({ theme }),
                    ':hover': {
                      color: theme.colors.primary,
                      textDecoration: 'none',
                    },
                    '&:last-child': {
                      marginBottom: '1px',
                    },
                  })}
                >
                  {item.name}
                </Link>
              ))}
            </Box>
          </Collapsable>
        </>
      ) : (
        <Link
          {...getLinkStyleProps(active)}
          ml="1px"
          fontWeight="semiBold"
          href={href}
          extend={({ theme }) => ({
            ...getCommonExtendStyles({ theme }),
            ':hover': {
              color: theme.colors.primary,
              textDecoration: 'none',
            },
          })}
        >
          {name}
        </Link>
      )}
    </Box>
  );
};

export default NavigationItem;
